import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import TextField from "@/components/ui/Textfield";
import Button from "@/components/ui/Button";
import useAuthStore from "@/store/authStore";

interface CompanyFormData {
  name: string;
  phone: string;
  email: string;
  url: string;
  address: string;
  address2: string;
  country: string;
  state: string;
  postalCode: string;
}

const CompanyDetails: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const media_agency_information = useAuthStore(
    (state) => state.media_agency_information
  );
  const [formData, setFormData] = useState<CompanyFormData>({
    name: "Time & Space",
    phone: "",
    email: "",
    url: "",
    address: "",
    address2: "",
    country: "",
    state: "",
    postalCode: "",
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleChange = (field: keyof CompanyFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    console.log("Saving company details:", formData);
    if (selectedFile) {
      console.log("Selected logo file:", selectedFile);
      // TODO: Upload the file to your server
      // const formDataToUpload = new FormData();
      // formDataToUpload.append('logo', selectedFile);
      // await uploadLogo(formDataToUpload);
    }
    alert("Changes saved!");
  };

  useEffect(() => {
    setFormData({
      ...formData,
      name: media_agency_information.name,
      email: media_agency_information.main_email,
      phone: media_agency_information.phone,
      url: media_agency_information.website,
      address: media_agency_information.billing_address.address_line_1,
      address2: media_agency_information.billing_address.address_line_2,
      country: media_agency_information.billing_address.country,
      postalCode: media_agency_information.billing_address.zip_postal,
      state: media_agency_information.billing_address.state,
    });
    // Set initial logo preview
    if (media_agency_information?.logo_url_signed) {
      setLogoPreview(media_agency_information.logo_url_signed);
    }
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">Account Preferences</h1>
      <h2 className="text-xl font-medium mb-6 flex items-center mt-10">
        <ChevronLeft
          className="w-6 h-6 text-[#EA33C0] mr-4 cursor-pointer"
          onClick={() => navigate("/account")}
        />
        Company Details
      </h2>

      <div className="border-t border-[#EA33C0] pt-8">
        <div className="flex gap-12">
          <div className="flex-1 space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">
                Company Name
              </label>
              <TextField
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Phone Number
              </label>
              <TextField
                type="text"
                placeholder="Enter company phone number"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <TextField
                type="email"
                placeholder="Enter the account email address"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Website</label>
              <TextField
                type="url"
                placeholder="Enter company website"
                value={formData.url}
                onChange={(e) => handleChange("url", e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Address</label>
              <TextField
                type="text"
                placeholder="Address"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                className="w-full"
              />
              <TextField
                type="text"
                placeholder="Address 2"
                value={formData.address2}
                onChange={(e) => handleChange("address2", e.target.value)}
                className="w-full mt-3"
              />
              <div className="flex gap-4 mt-3">
                <TextField
                  type="text"
                  placeholder="Country"
                  value={formData.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                  className="w-full"
                />
                <TextField
                  type="text"
                  placeholder="State/Province"
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  className="w-full"
                />
              </div>
              <TextField
                type="text"
                placeholder="Postal Code"
                value={formData.postalCode}
                onChange={(e) => handleChange("postalCode", e.target.value)}
                className="w-full mt-3"
              />
            </div>
          </div>

          <div className="w-96">
            <label className="block text-base font-medium mb-2">
              Company Logo
            </label>
            {logoPreview ? (
              <img
                src={logoPreview}
                className="w-full h-24 object-contain border rounded"
                alt="Agency Logo"
              />
            ) : (
              <div className="w-full h-24 border rounded flex items-center justify-center bg-gray-50">
                <span className="text-gray-400 text-sm">No logo uploaded</span>
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            <div className="flex justify-center">
              <button
                onClick={handleEditLogoClick}
                className="mt-4 text-sm font-medium underline hover:text-gray-700"
              >
                Edit Logo
              </button>
            </div>
            {selectedFile && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Selected: {selectedFile.name}
              </p>
            )}
          </div>
        </div>

        <div className="mt-12 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button type="primary" onClick={handleSave}>
              Save Changes
            </Button>
            <span className="text-gray-400 text-sm">
              Note: changes will apply across all your MediaBridge accounts.
            </span>
          </div>
          <span className="text-gray-400 text-sm">
            Account Created December 23, 2023
          </span>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
